import React, { useContext, useEffect, useState } from "react";
import { Drawer, Button, Typography, Space, Form, DatePicker } from "antd";
import { selectInterface } from "utilFunctions/shared/MultiSearchBar";
import { IUserInfo } from "utilFunctions/CommonInterfaces/userInterface";
import { UserInfoContext } from "Context/CommonContext";
import { DRS_STATUS } from "Pages/DRS/Constant/Constant";
import { DRS_INITIAL_DATE, useDRSContext } from "Pages/DRS/Context/DRSContext";
import useWindowSize from "utilFunctions/CustomHooks/useWindowSize";
import MutlSelectAntdComponent from "utilFunctions/shared/AntdSharedComponents/MutlSelectAntdComponent";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";
export const INITIAL_STATUS = ["PENDING", "REOPENED", "PICKED"];
const dateFormat = "YYYY/MM/DD";

const { Title } = Typography;
const { RangePicker } = DatePicker;

export interface AdvancedFilterValues {
  search?: string;
  stream?: string;
  subject?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  dateRange?: any;
  questionId?: string;
  difficulty?: string;
}

interface AdvancedFiltersProps {
  open: boolean;
  onClose: () => void;
  onApply: (values: AdvancedFilterValues) => void;
  onReset: () => void;
  form: any;
  type?: string;
}

const AdvancedFilters: React.FC<AdvancedFiltersProps> = ({
  open,
  onClose,
  onApply,
  onReset,
  form,
  type = "drsMyBucket",
}) => {
  const isDrsDashboard = type === "drsDashboard";
  const { screen }: any = useWindowSize();
  const { DSRFilters, drsDashboardFilters, individualFacultyFilters }: any =
    useDRSContext();
  const INITIAL_STATE_DRS = {
    statuses: INITIAL_STATUS,
    dateRange: [
      DRS_INITIAL_DATE.startDate
        ? dayjs(DRS_INITIAL_DATE.startDate, dateFormat)
        : null,
      DRS_INITIAL_DATE.endDate
        ? dayjs(DRS_INITIAL_DATE.endDate, dateFormat)
        : null,
    ],
  };
  const location = useLocation();
  const { pathname } = location;
  const isIndividualFaculty = pathname.includes("/DRS/faculty/");
  const currentFilters = isDrsDashboard
    ? isIndividualFaculty
      ? individualFacultyFilters
      : drsDashboardFilters
    : DSRFilters;

  const isSolvedByMe = currentFilters?.isSolvedByMe;

  const userInfo = useContext<IUserInfo>(UserInfoContext);

  const [stream, setStream] = useState<selectInterface[]>(
    userInfo?.userStreams ? userInfo?.userStreams : []
  );
  useEffect(() => {
    setStream(userInfo?.userStreams as any);
  }, [userInfo, userInfo?.userStreams]);

  useEffect(() => {
    if (open && currentFilters) {
      form.setFieldsValue({
        statuses: currentFilters.statuses,
        streamIds: currentFilters.streamIds,
        subjectIds: currentFilters.subjectIds,
        dateRange: DRS_INITIAL_DATE,
        yearMonthStart: currentFilters.yearMonthStart
          ? [
              dayjs(currentFilters.yearMonthStart),
              dayjs(currentFilters.yearMonthEnd),
            ]
          : null,
      });
    }
  }, [open, currentFilters, form]);

  const handleApply = () => {
    form.validateFields().then((values: any) => {
      console.log("Validated Values:", values);
      onApply(values);
    });
  };
  return (
    <Drawer
      title={<Title level={3}>Advanced Filters</Title>}
      placement="right"
      width={["xs", "sm", "md"].includes(screen) ? "100%" : 420}
      onClose={onClose}
      open={open}
      styles={{
        body: { paddingBottom: 80 },
      }}
    >
      <Form form={form} layout="vertical" initialValues={INITIAL_STATE_DRS}>
        {/* Date Range */}
        {!isDrsDashboard && (
          <Form.Item name="dateRange" label="Date Range">
            <RangePicker
              format={dateFormat}
              disabledDate={(current) => {
                // Disable future dates
                return current && current > dayjs().endOf("day");
              }}
              style={{ width: "100%" }}
              clearIcon={false}
            />
          </Form.Item>
        )}
        {/* Stream */}
        <Form.Item name="streamIds" label="Stream">
          <MutlSelectAntdComponent
            options={stream}
            placeholder="Select Stream"
            form={form}
            name={"streamIds"}
            maxTagCount={3}
          ></MutlSelectAntdComponent>
        </Form.Item>

        {/* Subject */}
        <Form.Item name="subjectIds" label="Subject">
          <MutlSelectAntdComponent
            options={userInfo?.subjectsListing}
            placeholder="Select Subject"
            form={form}
            name={"subjectIds"}
            maxTagCount={3}
          ></MutlSelectAntdComponent>
        </Form.Item>

        {/* Status */}
        {!isDrsDashboard && (
          <Form.Item name="statuses" label="Status">
            <MutlSelectAntdComponent
              options={DRS_STATUS}
              placeholder="Select Status"
              form={form}
              name={"statuses"}
              maxTagCount={3}
              disabled={isSolvedByMe}
            ></MutlSelectAntdComponent>
          </Form.Item>
        )}
        {isDrsDashboard && (
          <Form.Item label="Target Month" name="yearMonthStart">
            <RangePicker
              format={"MMM YY"}
              picker="month"
              minDate={dayjs().subtract(11, "months").startOf("month")}
              maxDate={dayjs().endOf("month")}
              size="large"
              style={{ width: "100%" }}
              allowClear={false}
            />
          </Form.Item>
        )}
      </Form>

      {/* Footer Buttons */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          padding: "16px",
          borderTop: "1px solid #eee",
          background: "#fff",
        }}
      >
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Button onClick={onReset}>Reset</Button>
          <Button type="primary" onClick={handleApply}>
            Apply Filters
          </Button>
        </Space>
      </div>
    </Drawer>
  );
};

export default AdvancedFilters;
